import { useEffect, useRef, useState } from "react";
import { PlusOutlined } from "@ant-design/icons";
import { UploadService } from "@/services/APIs";
import CustomButton from "../common/Button";
import { Trash } from "lucide-react";
import { notify } from "@/components/common/Notification";
import {
  AuthenticatedMediaImage,
  openAuthenticatedMediaPreview,
} from "@/components/common/AuthenticatedMediaImage";
import {
  IMAGE_UPLOAD_ACCEPT,
  IMAGE_UPLOAD_ERROR_TITLE,
  IMAGE_UPLOAD_HINT,
  IMAGE_UPLOAD_VALIDATION_MESSAGE,
  isAllowedImageUpload,
  pickImageUploadFiles,
} from "@/lib/imageUploadValidation";

type ImageUploadValue =
  | string
  | { id: string; url: string; name: string; size: number }
  | string[]
  | { id: string; url: string; name: string; size: number }[]
  | null
  | undefined;

type CustomImageUploadProps = {
  folderName?: string;
  onUploaded?: (response: any, file: File) => void;
  width?: number; // preferred simple width alias (px)
  boxWidth?: number;
  boxHeight?: number;
  disabled?: boolean;
  /** Ignored — only JPG/PNG allowed per {@link IMAGE_UPLOAD_ACCEPT}. */
  accept?: string;
  shape?: "square" | "circle";
  borderStyle?: "solid" | "dashed" | "dotted" | "double" | "hidden";
  multiple?: boolean;
  value?: ImageUploadValue;
  onChange?: (value: ImageUploadValue) => void;
  initialUrl?: string;
  className?: string;
};

const tryParseJsonArray = (raw: string): unknown[] | null => {
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

const FILE_UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const isUuidString = (s: string) => FILE_UUID_RE.test(String(s).trim());

const mediaPathFromUploadResponse = (response: Record<string, unknown> | null | undefined) => {
  const full = String(response?.file_path ?? response?.url ?? response?.file_url ?? "").trim();
  const thumb = String(
    response?.file_path_thumbnail ?? response?.thumbnail_url ?? response?.thumbnail ?? ""
  ).trim();
  return full || thumb;
};

const emitMultipleValue = (fileIds: string[]) => JSON.stringify(fileIds);

export const CustomImageUpload: React.FC<CustomImageUploadProps> = ({
  folderName = "images",
  onUploaded,
  width,
  boxWidth,
  boxHeight,
  disabled = false,
  borderStyle = "dashed",
  className,
  value,
  onChange,
  initialUrl,
  multiple = false,
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const DEFAULT_SIZE = 160;
  const W = width ?? boxWidth ?? DEFAULT_SIZE;
  const H = boxHeight ?? DEFAULT_SIZE;

  // states
  const [previewUrl, setPreviewUrl] = useState<string | null>(
    initialUrl ?? null
  );
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);
  const [uploadedFileIds, setUploadedFileIds] = useState<string[]>([]);
  const [isObjectPreview, setIsObjectPreview] = useState<boolean>(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isHovered, setIsHovered] = useState<number | null>(null);

  // Load existing files
  useEffect(() => {
    let isActive = true;
    const fetchFiles = async () => {
      if (
        !value ||
        (Array.isArray(value) && value.length === 0) ||
        value === "[]" ||
        value === ""
      ) {
        if (multiple) {
          setPreviewUrls([]);
          setUploadedFileIds([]);
        } else {
          setPreviewUrl(null);
        }
        return;
      }

      try {
        let parsedValue: ImageUploadValue = value;
        if (typeof value === "string") {
          const parsed = tryParseJsonArray(value);
          if (parsed) {
            parsedValue = parsed as string[];
          } else if (multiple && isUuidString(value)) {
            /* Single id stored as plain UUID string */
            parsedValue = [value.trim()];
          }
        }

        if (multiple && Array.isArray(parsedValue)) {
          const urls: string[] = [];
          const ids: string[] = [];
          for (const val of parsedValue) {
            const url = typeof val === "string" ? val : val?.url;
            const id = typeof val === "string" ? val : val?.id;
            if (!url && !id) {
              continue;
            }
            const fileId = id || (typeof url === "string" ? url : "");
            if (fileId) {
              ids.push(fileId);
            }
            const response = await UploadService.getFileById({
              id: (url as string) || fileId,
            });
            urls.push(mediaPathFromUploadResponse(response));
          }
          if (isActive) {
            setPreviewUrls(urls);
            setUploadedFileIds(ids);
          }
        } else if (!multiple) {
          let url: string;
          if (typeof value === "string") {
            url = value;
          } else if (value && typeof value === "object" && "url" in value) {
            url = value.url;
          } else {
            return;
          }
          const response = await UploadService.getFileById({
            id: url as string,
          });
          if (isActive) {
            setPreviewUrl(mediaPathFromUploadResponse(response));
          }
        }
      } catch (error) {
        console.error("Error fetching file details:", error);
      }
    };
    fetchFiles();
    return () => {
      isActive = false;
    };
  }, [value, multiple]);

  // cleanup
  useEffect(() => {
    return () => {
      if (isObjectPreview && previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [isObjectPreview, previewUrl]);

  const openPicker = async () => {
    if (disabled) return;
    const files = await pickImageUploadFiles(multiple, inputRef.current);
    await processSelectedFiles(files);
  };

  const extractUrl = (res: any): string | null =>
    res?.[0]?.file_path ??
    res?.file_path ??
    res?.data?.[0]?.url ??
    res?.files?.[0]?.url ??
    res?.url ??
    null;

  const extractId = (res: any): string | null =>
    res?.[0]?.id ??
    res?.id ??
    res?.pk ??
    res?.data?.[0]?.id ??
    res?.files?.[0]?.id ??
    null;

  const resetFileInput = (input: HTMLInputElement) => {
    input.value = "";
  };

  const pickAllowedImageFiles = (files: File[]): File[] => {
    const valid: File[] = [];
    for (const file of files) {
      if (isAllowedImageUpload(file)) {
        valid.push(file);
      } else {
        notify.error(IMAGE_UPLOAD_ERROR_TITLE, IMAGE_UPLOAD_VALIDATION_MESSAGE);
      }
    }
    return valid;
  };

  const processSelectedFiles = async (picked: File[]) => {
    if (!picked.length) {
      return;
    }

    if (multiple) {
      const validFiles = pickAllowedImageFiles(picked);

      if (validFiles.length === 0) {
        return;
      }

      try {
        const responseArray = await UploadService.uploadFiles({
          folderName,
          files: validFiles,
        });

        const newUrls: string[] = [];
        const newIds: string[] = [];

        for (let i = 0; i < responseArray.length; i++) {
          const resItem = responseArray[i];
          const id =
            (typeof resItem?.id === "string" && resItem.id) ||
            (typeof resItem?.pk === "string" && resItem.pk) ||
            (extractId(resItem) as string | null);
          let finalUrl =
            (typeof resItem?.file_path === "string" && resItem.file_path) ||
            extractUrl(resItem);

          if (!finalUrl && id) {
            try {
              const meta = await UploadService.getFileById({ id });
              finalUrl =
                meta?.file_path_thumbnail ?? meta?.file_path ?? null;
            } catch {
              /* ignore */
            }
          }
          if (!finalUrl && validFiles[i]) {
            finalUrl = URL.createObjectURL(validFiles[i]);
          }

          if (id) {
            newIds.push(id.trim());
            newUrls.push(typeof finalUrl === "string" ? finalUrl : "");
          } else if (
            finalUrl &&
            !/^https?:/i.test(finalUrl) &&
            isUuidString(finalUrl)
          ) {
            newIds.push(finalUrl.trim());
            newUrls.push("");
          }
        }

        const finalIds = [...uploadedFileIds, ...newIds];
        setUploadedFileIds(finalIds);
        setPreviewUrls([...previewUrls, ...newUrls]);

        onChange?.(emitMultipleValue(finalIds) as any);
        onUploaded?.(responseArray, validFiles[0]);
      } catch (error) {
        console.error("Upload failed:", error);
      }
    } else {
      const file = picked[0];
      if (!file || !isAllowedImageUpload(file)) {
        notify.error(IMAGE_UPLOAD_ERROR_TITLE, IMAGE_UPLOAD_VALIDATION_MESSAGE);
        return;
      }

      if (isObjectPreview && previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
      const tempUrl = URL.createObjectURL(file);
      setSelectedFile(file);
      setPreviewUrl(tempUrl);
      setIsObjectPreview(true);

      try {
        const list = await UploadService.uploadFiles({
          folderName,
          files: [file],
        });
        const first = list[0];
        const finalUrl =
          (typeof first?.file_path === "string" && first.file_path) ||
          extractUrl(first);
        let displayUrl = finalUrl;
        const fileId =
          (typeof first?.id === "string" && first.id) ||
          (typeof first?.pk === "string" && first.pk) ||
          (extractId(first) as string | null);

        if (!displayUrl && fileId) {
          try {
            const meta = await UploadService.getFileById({ id: fileId });
            displayUrl =
              meta?.file_path_thumbnail ?? meta?.file_path ?? null;
          } catch {
            /* ignore */
          }
        }

        if (displayUrl || fileId) {
          if (isObjectPreview && previewUrl) {
            URL.revokeObjectURL(previewUrl);
          }
          if (displayUrl) {
            setPreviewUrl(displayUrl);
          }
          setIsObjectPreview(false);
          onChange?.(fileId ?? null);
          onUploaded?.(list, file);
        }
      } catch (error) {
        console.error("Upload failed:", error);
      }
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    resetFileInput(e.target);
    await processSelectedFiles(files);
  };

  const clearSelection = (index?: number) => {
    if (multiple && index !== undefined) {
      const newUrls = previewUrls.filter((_, i) => i !== index);
      const newIds = uploadedFileIds.filter((_, i) => i !== index);
      setPreviewUrls(newUrls);
      setUploadedFileIds(newIds);

      const finalValue = emitMultipleValue(newIds);
      onChange?.(finalValue as any);
    } else {
      if (isObjectPreview && previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
      setSelectedFile(null);
      setPreviewUrl(null);
      setIsObjectPreview(false);
      onChange?.(null);
    }
  };

  // border styles
  const borderStyleClass =
    borderStyle === "dashed"
      ? "border-dashed"
      : borderStyle === "dotted"
        ? "border-dotted"
        : borderStyle === "double"
          ? "border-double"
          : borderStyle === "hidden"
            ? "border-none"
            : "";

  const borderWidthClass = borderStyle === "hidden" ? "border-0" : "border-2";
  const borderColorClass =
    borderStyle === "hidden" ? "" : "border-gray-300 hover:border-gray-400";

  // ✅ Conditional render
  return (
    <div className={`space-y-2 ${className}`}>
      <input
        ref={inputRef}
        type="file"
        accept={IMAGE_UPLOAD_ACCEPT}
        multiple={multiple}
        onChange={handleFileChange}
        style={{ display: "none" }}
      />
      {multiple ? (
        <div className="flex flex-wrap gap-3">
          {previewUrls.map((url, index) => (
            <div
              key={`${uploadedFileIds[index] ?? "p"}-${index}`}
              className="relative"
              style={{ width: W, height: H }}
              onMouseEnter={() => setIsHovered(index)}
              onMouseLeave={() => setIsHovered(null)}
            >
              <AuthenticatedMediaImage
                filePath={url}
                alt={`img-${index}`}
                style={{
                  width: W,
                  height: H,
                  objectFit: "cover",
                  borderRadius: 8,
                  border: "1px dashed #d9d9d9",
                }}
              />
              {isHovered === index && (
                <CustomButton
                  danger
                  icon={<Trash size={16} />}
                  type="text"
                  onClick={() => clearSelection(index)}
                  className="absolute right-1 top-1 rounded-full bg-white p-2 text-red-500 shadow"
                />
              )}
            </div>
          ))}

          {!disabled && (
            <div
              onClick={() => void openPicker()}
              className={[
                "flex cursor-pointer items-center justify-center rounded-md text-gray-400 transition hover:text-gray-600",
                borderWidthClass,
                borderStyleClass,
                borderColorClass,
              ].join(" ")}
              style={{ width: W, height: H }}
            >
              <PlusOutlined style={{ fontSize: 28 }} />
            </div>
          )}
        </div>
      ) : !previewUrl ? (
        <div
          role="button"
          tabIndex={0}
          onClick={() => void openPicker()}
          className={[
            "flex cursor-pointer items-center justify-center rounded-md text-gray-400 transition hover:text-gray-600",
            borderWidthClass,
            borderStyleClass,
            borderColorClass,
          ].join(" ")}
          style={{ width: W, height: H }}
        >
          <PlusOutlined style={{ fontSize: 28 }} />
        </div>
      ) : (
        <div
          className="relative cursor-pointer"
          style={{ width: W, height: H }}
          onMouseEnter={() => setIsHovered(0)}
          onMouseLeave={() => setIsHovered(null)}
          onClick={() => {
            if (previewUrl) {
              void openAuthenticatedMediaPreview(previewUrl);
            }
          }}
          role="button"
          tabIndex={0}
          onKeyDown={e => {
            if ((e.key === "Enter" || e.key === " ") && previewUrl) {
              void openAuthenticatedMediaPreview(previewUrl);
            }
          }}
        >
          <AuthenticatedMediaImage
            filePath={previewUrl}
            alt={selectedFile?.name ?? "image"}
            style={{
              width: W,
              height: H,
              objectFit: "cover",
              borderRadius: 8,
              border: "1px dashed #d9d9d9",
            }}
          />
          {isHovered === 0 && (
            <CustomButton
              danger
              icon={<Trash size={16} />}
              type="text"
              onClick={() => clearSelection()}
              className="absolute right-1 top-1 rounded-full bg-white p-2 text-red-500 shadow"
            />
          )}
        </div>
      )}
      {!disabled ? (
        <p className="text-xs text-gray-500">{IMAGE_UPLOAD_HINT}</p>
      ) : null}
    </div>
  );
};
